import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box } from "@mui/material";
import HPButton from "@/components/common/Button";
import HPInput from "@/components/common/Input";
import { useLazyGetDatasetQuestionsListQuery } from "@/store/api/dataset-api";
import type { QuestionDetail } from "@/models/Dataset";
import type { DatasetMetric } from "@models/Metric";
import type { ScorecardBrand, ScorecardDatasetMetricRow } from "@/models/Scorecard";
import { areDatasetMetricRowsEqual } from "@/utils/scorecard-row-lifecycle-utils";
import { useRowBrandMapping } from "@components/charts/scorecard-config/hooks/useRowBrandMapping";
import {
  buildDatasetMetricRowFetchFields,
  getMetricResultCandidates,
  isMetricEligibleForBrandMapping,
  metricNeedsQuestionDetailFetch,
} from "@components/charts/scorecard-config/dataset-metric-row-utils";
import DatasetMetricPickerList from "./DatasetMetricPickerList";
import DatasetMetricSelectedSummary from "./DatasetMetricSelectedSummary";
import BrandMappingStep from "../QuestionRowEditor/BrandMappingStep";

interface DatasetMetricRowBuilderProps {
  datasetId: string | null;
  metrics: DatasetMetric[];
  brands: ScorecardBrand[];
  editingRow?: ScorecardDatasetMetricRow | null;
  onCancel: () => void;
  onSave: (row: ScorecardDatasetMetricRow) => void;
}

/** Signature of the fetch-relevant fields a saved row cached at Save time —
 *  compared against a freshly-recomputed one to detect definition drift. */
function fetchSignature(fields: {
  sourceQuestionId: string;
  focusMode: string | null;
  metricValueCode: string | null;
  rollup: unknown;
  base: unknown;
  useTotalBase: boolean;
}): string {
  return JSON.stringify({
    sourceQuestionId: fields.sourceQuestionId,
    focusMode: fields.focusMode,
    metricValueCode: fields.metricValueCode,
    rollup: fields.rollup,
    base: fields.base,
    useTotalBase: fields.useTotalBase,
  });
}

/**
 * Inline Metric-row builder — the "Metric" side of the Row Source toggle
 * inside the shared RowBuilder shell. Lets the researcher pick a saved
 * Dataset Metric (read-only definition, resolved live from `metrics`) and
 * map its represented results to this Scorecard's brands. Reuses the same
 * Brand Mappings step/hook as a Question row — only the candidate-generation
 * adapter (dataset-metric-row-utils.ts) differs.
 *
 * Reopening an existing row always resolves the *current* metric definition
 * (selectedMetric/responseCategories are derived live, never from the row's
 * own cached snapshot) — this component's job on reopen is to surface that
 * revalidation to the researcher (definition-changed banner, ineligible/
 * missing-metric messaging) rather than silently reflect it.
 */
const DatasetMetricRowBuilder: React.FC<DatasetMetricRowBuilderProps> = ({
  datasetId,
  metrics,
  brands,
  editingRow,
  onCancel,
  onSave,
}) => {
  const initialMetric = editingRow
    ? (metrics.find((m) => m.id === editingRow.datasetMetricId) ?? null)
    : null;
  const metricWasRemoved = !!editingRow && !initialMetric;

  // A saved label equal to the row's OLD cached metric-name snapshot means
  // the researcher never customized it — safe to keep tracking it as a
  // default so a metric rename can still refresh it below. Any other saved
  // label is treated as a customization and is never touched again.
  const wasDefaultLabel = !!editingRow && editingRow.label === editingRow.datasetMetricName;
  const initialLabel =
    wasDefaultLabel && initialMetric && initialMetric.name !== editingRow?.datasetMetricName
      ? initialMetric.name
      : (editingRow?.label ?? "");

  const [selectedMetric, setSelectedMetric] = useState<DatasetMetric | null>(initialMetric);
  const [isPicking, setIsPicking] = useState(!initialMetric);
  const [searchTerm, setSearchTerm] = useState("");
  const [questionDetail, setQuestionDetail] = useState<QuestionDetail | null>(null);
  const [fetchError, setFetchError] = useState(false);
  const [label, setLabel] = useState(initialLabel);
  const [labelTouched, setLabelTouched] = useState(!!editingRow && !wasDefaultLabel);

  const [fetchQuestionDetails, { isFetching: isFetchingDetails }] =
    useLazyGetDatasetQuestionsListQuery();

  const runFetchQuestionDetails = (metric: DatasetMetric) => {
    if (!metric.source || !datasetId) return;
    const question = metric.source.question;
    setFetchError(false);
    fetchQuestionDetails({
      id: datasetId,
      page: 1,
      page_size: 10,
      search_query: question.questionLabel,
      dataset_element_id: question.questionId,
      expanded: true,
    })
      .unwrap()
      .then((result) => {
        const detail = result.questions.find((q) => q.datasetElementId === question.questionId);
        setQuestionDetail(detail ?? null);
      })
      .catch(() => setFetchError(true));
  };

  // Resolve the underlying question detail for metric kinds whose candidate
  // generation needs it (see metricNeedsQuestionDetailFetch) — both on
  // initial load (editing an existing row) and whenever a new metric is picked.
  useEffect(() => {
    if (!selectedMetric?.source || !datasetId || !metricNeedsQuestionDetailFetch(selectedMetric)) {
      setQuestionDetail(null);
      setFetchError(false);
      return;
    }
    runFetchQuestionDetails(selectedMetric);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMetric?.id, datasetId]);

  const handleRetryFetch = () => {
    if (selectedMetric) runFetchQuestionDetails(selectedMetric);
  };

  const handleSelectMetric = (metric: DatasetMetric) => {
    setSelectedMetric(metric);
    setIsPicking(false);
    if (!labelTouched) setLabel(metric.name);
  };

  const responseCategories = useMemo(
    () => (selectedMetric ? getMetricResultCandidates(selectedMetric, questionDetail) : []),
    [selectedMetric, questionDetail],
  );

  const { mappings, onManualBind, onMarkUnavailable, onRestoreMapping } = useRowBrandMapping(
    brands,
    responseCategories,
    editingRow,
  );

  const eligible = selectedMetric ? isMetricEligibleForBrandMapping(selectedMetric).eligible : false;
  const ineligibleReason = selectedMetric
    ? isMetricEligibleForBrandMapping(selectedMetric).reason
    : undefined;
  const needsFetch = selectedMetric ? metricNeedsQuestionDetailFetch(selectedMetric) : false;
  const canSave =
    !!selectedMetric &&
    eligible &&
    (!needsFetch || !!questionDetail) &&
    !isFetchingDetails &&
    label.trim().length > 0;

  // Only meaningful while still viewing the SAME metric reference this row
  // was saved against — and only once a required question-detail fetch has
  // resolved, so a mid-flight fetch never flashes a false-positive banner.
  const definitionChanged = useMemo(() => {
    if (!editingRow || !selectedMetric?.source || selectedMetric.id !== editingRow.datasetMetricId) {
      return false;
    }
    if (needsFetch && !questionDetail) return false;
    const freshFields = buildDatasetMetricRowFetchFields(selectedMetric, questionDetail);
    const oldSignature = fetchSignature({
      sourceQuestionId: editingRow.sourceQuestionId,
      focusMode: editingRow.focusMode,
      metricValueCode: editingRow.metricValueCode,
      rollup: editingRow.rollup,
      base: editingRow.base,
      useTotalBase: editingRow.useTotalBase,
    });
    const newSignature = fetchSignature({
      sourceQuestionId: freshFields.sourceQuestionId,
      focusMode: freshFields.focusMode,
      metricValueCode: freshFields.metricValueCode,
      rollup: freshFields.rollup,
      base: selectedMetric.base,
      useTotalBase: freshFields.useTotalBase,
    });
    return oldSignature !== newSignature;
  }, [editingRow, selectedMetric, questionDetail, needsFetch]);

  const buildRow = (): ScorecardDatasetMetricRow | null => {
    if (!selectedMetric || !selectedMetric.source) return null;
    const fetchFields = buildDatasetMetricRowFetchFields(selectedMetric, questionDetail);
    return {
      id: editingRow?.id ?? crypto.randomUUID(),
      type: "metric",
      datasetMetricId: selectedMetric.id,
      datasetMetricName: selectedMetric.name,
      label: label.trim(),
      resultType: selectedMetric.source.resultType,
      brandMappings: mappings,
      responseCategories,
      base: selectedMetric.base,
      ...fetchFields,
    };
  };

  const pendingRow = buildRow();
  const isUnchanged = !!editingRow && !!pendingRow && areDatasetMetricRowsEqual(pendingRow, editingRow);

  const handleSave = () => {
    if (!pendingRow) return;
    onSave(pendingRow);
  };

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        {metricWasRemoved && isPicking && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This row's saved metric is no longer available. Choose a replacement.
          </Alert>
        )}
        {isPicking || !selectedMetric ? (
          <DatasetMetricPickerList
            datasetId={datasetId}
            metrics={metrics}
            searchTerm={searchTerm}
            onSearchTermChange={setSearchTerm}
            selectedMetricId={selectedMetric?.id ?? ""}
            onSelectMetric={handleSelectMetric}
          />
        ) : (
          <DatasetMetricSelectedSummary
            datasetId={datasetId}
            metric={selectedMetric}
            onChange={() => setIsPicking(true)}
          />
        )}
      </Box>

      {selectedMetric && !isPicking && !eligible && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {ineligibleReason}
        </Alert>
      )}

      {selectedMetric && eligible && !isPicking && (
        <>
          {definitionChanged && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This metric's definition has changed since this row was last saved. Mappings below
              reflect the current definition — review and save to update this row.
            </Alert>
          )}

          {fetchError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={
                <HPButton variant="outlined" size="small" onClick={handleRetryFetch}>
                  Retry
                </HPButton>
              }
            >
              Failed to load this metric's question details.
            </Alert>
          )}

          <Box sx={{ mb: 2 }}>
            <HPInput
              label="Row Label*"
              value={label}
              onChange={(e) => {
                setLabel(e.target.value);
                setLabelTouched(true);
              }}
              placeholder="Row label"
            />
          </Box>

          <BrandMappingStep
            brands={brands}
            mappings={mappings}
            responseCategories={responseCategories}
            onManualBind={onManualBind}
            onMarkUnavailable={onMarkUnavailable}
            onRestoreMapping={onRestoreMapping}
          />
        </>
      )}

      <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
        <Box sx={{ flex: 1 }}>
          <HPButton variant="outlined" size="small" className="w-full" onClick={onCancel}>
            Cancel
          </HPButton>
        </Box>
        <Box sx={{ flex: 1 }}>
          <HPButton
            variant="contained"
            size="small"
            className="w-full"
            onClick={handleSave}
            disabled={!canSave || isUnchanged}
          >
            {editingRow ? "Apply changes" : "Add row"}
          </HPButton>
        </Box>
      </Box>
    </Box>
  );
};

export default DatasetMetricRowBuilder;
